import { Color } from '@coast/schemas';
import { isNil, isNumber, isString } from 'lodash';
import { CSSProperties, ReactNode } from 'react';
import { View as RNView, StyleProp, TouchableOpacity, ViewStyle } from 'react-native';
import { Button } from '@shared/components/Button';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBLoadingIndicator } from '@shared/components/loading';
import { Colors, grid } from '@shared/styles';
import { Styles } from './PillStyles';

export type PillColor = Color | 'white' | 'transparent' | 'backgroundGrayLight';

interface Props {
  children?: ReactNode;
  sections?: ReactNode[];
  noBorder?: boolean;
  backgroundColor?: PillColor;
  round?: boolean;
  maxWidth?: ViewStyle['maxWidth'];
  numberOfLines?: number;
  onRemove?(): void;
  italic?: boolean;
  noLeftMargin?: boolean;
  noRightMargin?: boolean;
  borderColor?: string;
  borderWidth?: number;
  style?: StyleProp<ViewStyle>;
  textStyle?: CSSProperties;
  onPress?: VoidFunction;
  loading?: boolean;
  strikeThrough?: boolean;
}

export function Pill({
  children,
  sections = [children],
  backgroundColor = 'backgroundGrayLight',
  round = false,
  noBorder = false,
  maxWidth = grid(32),
  numberOfLines = 1,
  onRemove,
  italic,
  noLeftMargin = false,
  noRightMargin = false,
  borderColor,
  borderWidth,
  style,
  textStyle,
  onPress,
  loading = false,
  strikeThrough = false,
}: Readonly<Props>) {
  const isOneLine = numberOfLines === 1;

  const styles = [
    Styles.pill,
    italic && Styles.italic,
    round && Styles.round,
    noBorder && Styles.noBorder,
    noLeftMargin && Styles.noLeftMargin,
    noRightMargin && Styles.noRightMargin,
    isOneLine && Styles.oneLine,
    { backgroundColor: Colors.v2[backgroundColor] },
    !isNil(borderColor) && { borderColor },
    !isNil(borderWidth) && { borderWidth },
    style,
  ];

  return (
    <TouchableOpacity onPress={onPress} style={styles} disabled={loading || isNil(onPress)}>
      {loading && (
        <MBView style={Styles.loading}>
          <MBLoadingIndicator />
        </MBView>
      )}
      {sections.map((section, index) => (
        <PillSection
          key={index}
          strikeThrough={strikeThrough}
          style={[
            Styles.pillSection,
            index > 0 && Styles.notFirstSection,
            { maxWidth },
            loading && Styles.disabled,
          ]}
          italic={italic}
          numberOfLines={numberOfLines}
          textStyle={textStyle}
        >
          {section}
        </PillSection>
      ))}
      {onRemove && (
        <View stopPropagation>
          <Button
            onClick={!loading ? onRemove : undefined}
            variant="ghost"
            size="small"
            icon="exit"
            iconSize={grid(2)}
            iconOnly
          />
        </View>
      )}
    </TouchableOpacity>
  );
}

function PillSection({
  children,
  numberOfLines,
  italic,
  strikeThrough,
  style,
  textStyle,
}: Pick<Props, 'children' | 'numberOfLines' | 'italic' | 'strikeThrough' | 'style' | 'textStyle'>) {
  const isPrimitiveChild = isString(children) || isNumber(children);

  return (
    <RNView style={style}>
      {isPrimitiveChild && (
        <Text
          flex
          size="body1"
          numberOfLines={numberOfLines}
          italic={italic}
          lineThrough={strikeThrough}
          style={textStyle}
          color={italic ? Colors.v2.gray50 : undefined}
        >
          {children}
        </Text>
      )}
      {!isPrimitiveChild && children}
    </RNView>
  );
}
